import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';

import useHawksearchClientData from './useHawksearchClientData';
import { isNonFacetPersistentQueryString, getQueryStringFromSearchObj, constructRelativeSrpUrlFromSearchObj } from './util';
import { formatMetricValue } from '../metrics/metrics-helpers';
import useHawksearchComponent from './useHawksearchComponent';
import { fetchHawksearchResults } from './actions';

export default () => {
    const hawksearchResults = useHawksearchComponent();
    const history = useHistory();
    const customClientData = useHawksearchClientData();

    const hawksearchSearchData = hawksearchResults && hawksearchResults[hawksearchResults.type];
    const { currentSearch, Sorting } = hawksearchSearchData || {}

    const dispatch = useDispatch();
    const setSearch = (queryObj, options) => {
        try{
            let { isPaginating } = options || {};

            if(isPaginating){
                return dispatch(fetchHawksearchResults(queryObj, { isPaginating })) // if paginating, don't add the page number on the URL
            }

            let newUrl = constructRelativeSrpUrlFromSearchObj(queryObj);
            history.push(newUrl);
        }catch(e){}
    };

    const getNextPage = debounce( queryObj => setSearch(queryObj, { isPaginating: true }), 300);

    const toggleFacetValue = (facetKey, facetValue) => {
        // generate new search object
        const newSearch = currentSearch ? cloneDeep(currentSearch) : {};
        newSearch.FacetSelections = newSearch.FacetSelections || {};
        const newFacetSelections = newSearch.FacetSelections;
        const hasFacetKeySelection = Array.isArray(newFacetSelections[facetKey]);
        const fvIndex = hasFacetKeySelection && newFacetSelections[facetKey].indexOf(facetValue);

        if(typeof fvIndex === 'number' && fvIndex > -1) newFacetSelections[facetKey].splice(fvIndex, 1); // if it hs the facetValue, toggle off
        else if(!hasFacetKeySelection) newFacetSelections[facetKey] = [facetValue]; // if none of the filter's options are selected
        else if(fvIndex === -1) newFacetSelections[facetKey].push(facetValue); // if atleast one of the filter's options is selected

        // generate new URL based on new search object. useHawksearchQueryStringListener() will update the results.
        let newUrl = constructRelativeSrpUrlFromSearchObj(newSearch);
        history.push(newUrl);
    };


    const currentSearchWithClientData = {
        ...(currentSearch || {}),
        ClientData: customClientData,
    };

    let sortingOptions = [],
        selectedSortOption;

    let availableSortOptions = Sorting && Array.isArray(Sorting.Items) ? Sorting.Items : [];

    const onSort = (...args) => {
        try {
            let [value] = args;
            let newSearch = {
                ...(currentSearch || {}),
                SortBy: value,
            };
            if (newSearch.PageNo) {
                newSearch.PageNo = 1;
            }
            setSearch(newSearch);
        } catch (e) {}
    };

    for (let availableSortOption of availableSortOptions) {
        let { Value, Label, Selected } = availableSortOption || {};
        if (Value && Label) {
            sortingOptions.push({
                label: Label,
                value: Value,
                title: Label,
                key: Value,
                gtmSort: formatMetricValue(Label),
            });
            selectedSortOption = Selected ? Value : selectedSortOption;
        }
    }

    // price range updates
    const priceRangeSelector = useCallback(field =>
        debounce(data => {
            try {
                setSearch({
                    ...currentSearchWithClientData,
                    FacetSelections: {
                        ...((currentSearch && currentSearch.FacetSelections) || {}),
                        [field]: [data.join(',')],
                    },
                });
            } catch (e) {}
        }, 350)
    );

    // clearing filters only, but keeping keyword and query, etc.
    const clearFilters = () => {
        const { type } = hawksearchResults || {};
        const persistingQuery = type === 'content' ? {
            FacetSelections: { type: ['Content'] }
        } : { query: 'type:product' }
        setSearch({
            ...currentSearchWithClientData,
            FacetSelections: {},
            ...persistingQuery
        })
    };

    const facetSelector = (facetKey, facetValue, facets) => () => {
        try {
            toggleFacetValue(facetKey, facetValue);
        } catch (e) {}
    };

    /**
     *
     * @param {*} category to select
     * Resets the entire search.
     */
    const setCategory = (category, disable, replaceAll) => {
        const { FacetSelections, Keyword } = currentSearchWithClientData || {};
        const categoryKey = 'dte_facet_category';
        const newFacetSelections =
            FacetSelections ?
            Object.keys(FacetSelections).reduce((facets, key) => {
                if (isNonFacetPersistentQueryString(key) || key === categoryKey) {
                    facets[key] = FacetSelections[key];
                }
                return facets;
            }, {}) : {}

        const categoryIsObject = typeof category === 'object' && category;
        
        try{
            if(categoryIsObject){
                newFacetSelections[categoryKey] = Object.keys(category);
            }
            else if(!replaceAll){
                newFacetSelections[categoryKey] = Array.isArray(newFacetSelections[categoryKey] ) ? newFacetSelections[categoryKey].filter(cat => cat !== category) : [];
                if(!disable) {
                    newFacetSelections[categoryKey].push(category);
                }
                else if(newFacetSelections[categoryKey].length === 0){
                    delete newFacetSelections[categoryKey];

                }
            }
            else if(disable && replaceAll){
                delete newFacetSelections[categoryKey] ;
            }else if(!disable && replaceAll){
                newFacetSelections[categoryKey] = [category]
            }
        }catch(e){}

        setSearch({
            ClientData: customClientData,
            ...newFacetSelections && { FacetSelections: newFacetSelections },
            ...Keyword && { Keyword }
        });
    };

    const debouncedSetCategory = useCallback(
        debounce(setCategory, 400),
        [setCategory]
    )

    const hawksearchProps = {
        selectedSortOption,
        sortingOptions,
        onSort,
        hawksearchSearchData,
        setSearch,
        priceRangeSelector,
        facetSelector,
        clearFilters,
        currentSearch: currentSearchWithClientData,
        setCategory,
        getNextPage,
        debouncedSetCategory
    };

    return hawksearchProps;
};
